import {merge} from 'lodash';
import {
  OVRecommendActionEnum,
  GenerateOVRPotBatchArguments,
  ComboFrequencyEnum,
} from '../../../schema-types/globalTypes';
import validateUUID from '../../../utils/validateUUID';
import {
  PLAN_TYPE_DESTINATION,
  PLAN_TYPE_SOURCE,
} from '../CreateRecommend/RecommendSourceDestination/constants';
import {
  createOrUpdateSubplan,
  getPotContactId,
  updateRecommendPotsVariables,
  updateRecsVarsProps,
  getIsMinor,
} from './helpers';

import {RECOMMENDATION_SUBTYPE_TO_SERVER} from '../../../constants/SolutionType';
import {POTS_CASH} from '../../../utils/potsFilter';
import {contributionIsActuallyTransfer} from '../RecommendsContribute/ContributeHelpers';

import {getComboFrequency} from '../../../hooks/useComboFrequency';
import {getISODateOrUndefined} from '../../../utils/getISODate';
import {getPlanFeesServerFromFormik} from '../CreateRecommend/PlanFees';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {contributePlanAndSubplanAmounts} from '../RecommendsContribute/contributeFields';

import {toUUID} from '../../../graphql/uuid';
import {UpsertOvrecommendVariables} from '../../../schema-types/UpsertOvrecommend';
import {getFeeServerFromFormik} from '../CreateRecommend/DAInitialFees';
import {comboToFrequencyMapping} from '../hooks/contributeHooks';

/**
 * PLEASE NOTE: source pots: "contribute from" and destination pots: "contribute to"
 */

const getContributionVariables = async ({
  accountId,
  goalId,
  pots,
  ovrecommendId,
  values,
  recommendDetails,
  contacts = [],
}: updateRecsVarsProps): Promise<UpsertOvrecommendVariables> => {
  const {contribute, contact, urgency, urgencyReason} = values;
  const isMinor = getIsMinor(contact, contacts);
  if (!contribute) {
    throw Error('no contribute');
  }

  const newDestinationPlan = contribute?.contributeTo === 'newPlan';

  const destinationPot = pots?.find((x) => x.id === contribute?.contributeTo);

  const productProviderId = newDestinationPlan
    ? contribute?.newPlan?.planProvider
    : destinationPot?.productProvider?.id;

  const productNameId = newDestinationPlan
    ? contribute?.newPlan?.planType
    : destinationPot?.productName?.id;
  //
  //
  // DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION
  //
  //

  return {
    action: OVRecommendActionEnum.CONTRIBUTE,
    ovrecommendId: validateUUID(ovrecommendId),
    accountId: validateUUID(accountId),
    goalId: validateUUID(goalId),
    isNewPlanRequired: newDestinationPlan,
    contactId: getPotContactId(contact, isMinor),
    // startDate MUST BE an ISO string
    // https://the-guild.dev/graphql/scalars/docs/scalars/date-time
    startDate:
      getISODateOrUndefined(contribute?.contributeFrom?.start) ||
      getISODateOrUndefined(recommendDetails?.startDate),
    productProviderId,
    productNameId,
    urgency,
    urgencyReason,
    solutionType: RECOMMENDATION_SUBTYPE_TO_SERVER.PRODUCT_BASED,
    parentGuardianId: newDestinationPlan
      ? validateUUID(contribute.newPlan?.parentGuardianId)
      : undefined,
  };
};

export const generateContributionOVRPots = async ({
  accountId,
  pots,
  ovrecommendId,
  values,
  generateRecommendationPots,
  recommendDetails,
  reviewId,
  contacts = [],
}: updateRecommendPotsVariables) => {
  const potArgs: GenerateOVRPotBatchArguments[] = [];
  const {contribute, contact} = values;
  const isMinor = getIsMinor(contact, contacts);
  if (!contribute) {
    throw Error('no contribute');
  }

  const previousTo = recommendDetails?.pots?.find(
    (x) => x?.planType === PLAN_TYPE_DESTINATION
  );
  const newDestinationPlan = contribute?.contributeTo === 'newPlan';

  const previousFrom = recommendDetails?.pots?.find(
    (x) => x?.planType === PLAN_TYPE_SOURCE
  );
  const isCash =
    contribute?.contributeFrom?.id === POTS_CASH || previousFrom?.pot?.isCash;
  const destinationPot = pots?.find((x) => x.id === contribute?.contributeTo);

  const productProviderId = newDestinationPlan
    ? contribute?.newPlan?.planProvider
    : destinationPot?.productProvider?.id;

  const productNameId = newDestinationPlan
    ? contribute?.newPlan?.planType
    : destinationPot?.productName?.id;
  //
  //
  // DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION
  //
  //

  const isActuallyTransfer = contributionIsActuallyTransfer({
    values,
    pots: pots || [],
  });
  const inSpecie = isActuallyTransfer ? contribute.contributeFrom?.inSpecie : false;

  let potId = null;
  if (!isCash) {
    if (contribute.contributeFrom?.id) {
      potId = toUUID(contribute.contributeFrom.id);
    }
  }

  const recurringClientFrequency =
    comboToFrequencyMapping[
      values.contribute?.comboFrequency ?? ComboFrequencyEnum.SINGLE
    ];

  const sourceVariables: GenerateOVRPotBatchArguments = {
    potId,
    planType: PLAN_TYPE_SOURCE,
    recurringClientFrequency,
    singleClientAmount: getContributeAmountValue(
      values,
      contribute.contributeFrom!,
      'singleClientAmount'
    ),
    recurringClientAmount: getContributeAmountValue(
      values,
      contribute.contributeFrom!,
      'recurringClientAmount'
    ),
    inSpecie,
    full: contribute.contributeFrom?.full || false,
    initialFee: values.isSppDa ? undefined : contribute.contributeFrom?.initialFee,
    legacyInitialFee: getFeeServerFromFormik(
      values.isSppDa,
      contribute.contributeFrom?.legacyInitialFee
    ),
    initialEmployerFee: contribute.contributeFrom?.initialEmployerFee,
    legacyInitialEmployerFee: getFeeServerFromFormik(
      values.isSppDa,
      contribute.contributeFrom?.legacyInitialEmployerFee
    ),
  };

  if (isCash) {
    const cashVariables: Partial<GenerateOVRPotBatchArguments> = {
      isDummy: true,
      isCash: true,
      reviewId: toUUID(reviewId),
      initialFee: contribute.contributeFrom?.initialFee,
      initialEmployerFee: contribute.contributeFrom?.initialEmployerFee,
      legacyInitialFee: getFeeServerFromFormik(
        values.isSppDa,
        contribute.contributeFrom?.legacyInitialFee
      ),
      legacyInitialEmployerFee: getFeeServerFromFormik(
        values.isSppDa,
        contribute.contributeFrom?.legacyInitialEmployerFee
      ),
      full: contribute.contributeFrom?.full || false,
      originalOvrId: toUUID(ovrecommendId),
      contactId: getPotContactId(contact, isMinor),
      potId: contribute.contributeFrom?.cashPotId
        ? toUUID(contribute.contributeFrom.cashPotId)
        : null,
    };
    merge(sourceVariables, cashVariables);
  }

  const sourcePensionVariables: Partial<GenerateOVRPotBatchArguments> =
    values.mainPlanType === 'pension'
      ? {
          singleEmployerAmount: getContributeAmountValue(
            values,
            contribute.contributeFrom!,
            'singleEmployerAmount'
          ),
          recurringEmployerAmount: getContributeAmountValue(
            values,
            contribute.contributeFrom!,
            'recurringEmployerAmount'
          ),
          recurringEmployerFrequency: contribute.contributeFrom?.recurringEmployerAmount
            ? recurringClientFrequency
            : undefined,

          initialEmployerFee: contribute.contributeFrom?.initialEmployerFee,
        }
      : {};

  potArgs.push({
    ...sourceVariables,
    ...sourcePensionVariables,
  });

  //
  //
  // DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION DESTINATION
  //
  //

  const pensionVariables: Pick<
    GenerateOVRPotBatchArguments,
    'singleEmployerAmount' | 'recurringEmployerAmount' | 'recurringEmployerFrequency'
  > =
    values.mainPlanType === 'pension'
      ? {
          singleEmployerAmount: getContributeAmountValue(
            values,
            contribute.destinationValues,
            'singleEmployerAmount'
          ),
          recurringEmployerAmount: getContributeAmountValue(
            values,
            contribute.destinationValues,
            'recurringEmployerAmount'
          ),
          recurringEmployerFrequency: contribute.destinationValues.recurringEmployerAmount
            ? recurringClientFrequency
            : undefined,
        }
      : {};

  if (newDestinationPlan) {
    const newPlanVariables: GenerateOVRPotBatchArguments = {
      isDummy: true,
      potId: contribute.newPlan?.existingPotId
        ? toUUID(contribute.newPlan?.existingPotId)
        : null,
      reviewId: toUUID(reviewId),
      planType: PLAN_TYPE_DESTINATION,
      recurringClientFrequency,
      singleClientAmount: getContributeAmountValue(
        values,
        contribute.contributeFrom!,
        'singleClientAmount'
      ),
      recurringClientAmount: getContributeAmountValue(
        values,
        contribute.contributeFrom!,
        'recurringClientAmount'
      ),
      full: contribute.contributeFrom?.full || false,
      fees: getPlanFeesServerFromFormik(contribute.newPlan?.planFees), // ongoing fee
      hasFeeDeviation: contribute.newPlan?.planFees.feeDeviation || false,
      mandate:
        typeof contribute?.newPlan?.model === 'string'
          ? contribute?.newPlan?.model
          : null,
      modelPortfolioId:
        typeof contribute?.newPlan?.model === 'string'
          ? null
          : validateUUID(contribute?.newPlan?.model?.id || ''),
      productProviderId,
      productNameId,
      originalOvrId: toUUID(ovrecommendId),
      inTrust: contribute.newPlan?.inTrust,
      contactId: getPotContactId(contact, isMinor),
      ...pensionVariables,
    };
    potArgs.push(newPlanVariables);

    if (contribute.newPlan?.subplan0?.checked) {
      // createOrUpdateSubplan reads amounts from subplanObj (model/fees config),
      // which has no contribution amounts for new-plan subplans — override from contributeFrom.
      potArgs.push({
        ...createOrUpdateSubplan({
          subplanObj: contribute.newPlan!.subplan0!,
          isDummy: true,
          reviewId,
          frequency: recurringClientFrequency,
          accountId,
          ovrecommendId,
          childPotId: contribute?.newPlan?.subplan0?.existingPotId || null,
          contactId: getPotContactId(contact, isMinor),
        }),
        singleClientAmount: getContributeAmountValue(
          values,
          contribute.contributeFrom!,
          'singleClientAmount'
        ),
        recurringClientAmount: getContributeAmountValue(
          values,
          contribute.contributeFrom!,
          'recurringClientAmount'
        ),
        ...pensionVariables,
      });
    }

    if (contribute.newPlan?.subplan1?.checked) {
      // same reason as subplan0 above
      potArgs.push({
        ...createOrUpdateSubplan({
          subplanObj: contribute.newPlan!.subplan1!,
          isDummy: true,
          reviewId,
          frequency: recurringClientFrequency,
          accountId,
          childPotId: contribute?.newPlan?.subplan1?.existingPotId || null,
          ovrecommendId,
          contactId: getPotContactId(contact, isMinor),
        }),
        singleClientAmount: getContributeAmountValue(
          values,
          contribute.contributeFrom!,
          'singleClientAmount'
        ),
        recurringClientAmount: getContributeAmountValue(
          values,
          contribute.contributeFrom!,
          'recurringClientAmount'
        ),
        ...pensionVariables,
      });
    }
  } else {
    const destinationPotId = contribute?.contributeTo || previousTo?.pot?.id;
    // if destination pot
    // THIS IS FOR TOP LEVEL MAIN PLANS

    const existingDestinationPotVariables: GenerateOVRPotBatchArguments = {
      potId: destinationPotId,
      planType: PLAN_TYPE_DESTINATION,
      recurringClientFrequency,
      singleClientAmount: getContributeAmountValue(
        values,
        contribute.destinationValues,
        'singleClientAmount'
      ),
      recurringClientAmount: getContributeAmountValue(
        values,
        contribute.destinationValues,
        'recurringClientAmount'
      ),
      full: contribute.contributeFrom?.full || false,
      // fee: getInitialFeeServerFromFormik(contribute.destinationValues),
      singleEmployerAmount: getContributeAmountValue(
        values,
        contribute.destinationValues,
        'singleEmployerAmount'
      ),
      recurringEmployerAmount: getContributeAmountValue(
        values,
        contribute.destinationValues,
        'recurringEmployerAmount'
      ),
    };
    potArgs.push(existingDestinationPotVariables);

    const subplans = Object.entries(contribute.destinationValues.subplans || {}).filter(
      ([, fields]) => fields.checked
    );
    if (subplans?.length) {
      // loop through subplans,using subplanid instead of top plan id. Do not edit main plan
      for (const [subPlanId, subplanForm] of subplans) {
        if (subplanForm) {
          const frequency = recurringClientFrequency;
          if (ovrecommendId) {
            debugger;
          }
          potArgs.push({
            potId: toUUID(subPlanId),
            planType: PLAN_TYPE_DESTINATION,
            singleClientAmount: getContributeAmountValue(
              values,
              subplanForm,
              'singleClientAmount'
            ),
            singleEmployerAmount: getContributeAmountValue(
              values,
              subplanForm,
              'singleEmployerAmount'
            ),
            recurringClientAmount: getContributeAmountValue(
              values,
              subplanForm,
              'recurringClientAmount'
            ),
            recurringEmployerAmount: getContributeAmountValue(
              values,
              subplanForm,
              'recurringEmployerAmount'
            ),
            recurringEmployerFrequency: subplanForm?.recurringEmployerAmount // if no employer amount, dont provide frequency
              ? frequency
              : undefined,
            recurringClientFrequency: frequency,
            productProviderId: subplanForm?.planProvider,
            // fee,
          });
        }
      }
    }
  }
  await generateRecommendationPots({
    variables: {
      accountId: validateUUID(accountId)!,
      ovrecommendId: validateUUID(ovrecommendId)!,
      args: potArgs,
    },
  });
};

export default getContributionVariables;

function getContributeAmountValue(
  values: recommendFieldsType,
  obj: Partial<contributePlanAndSubplanAmounts>,
  amountType: keyof contributePlanAndSubplanAmounts
) {
  const valUnformatted = obj[amountType];
  const valFormatted = valUnformatted === 'NaN' ? 0 : Number(valUnformatted) || 0;

  const {hasRecurring, hasSingle} = getComboFrequency(values.contribute?.comboFrequency);
  if (amountType === 'recurringClientAmount') {
    if (!hasRecurring) {
      return undefined;
    }
  }
  if (amountType === 'recurringEmployerAmount') {
    if (!hasRecurring) {
      return undefined;
    }
  }
  if (amountType === 'netRecurringClientAmount') {
    if (!hasRecurring) {
      return undefined;
    }
  }
  if (amountType === 'singleClientAmount') {
    if (!hasSingle) {
      return undefined;
    }
  }
  if (amountType === 'singleEmployerAmount') {
    if (!hasSingle) {
      return undefined;
    }
  }
  if (amountType === 'netSingleClientAmount') {
    if (!hasSingle) {
      return undefined;
    }
  }
  return valFormatted;
}
